// RigSentinel Context Engine — Phase RCE-1
//
// MIGRATION WIZARD support
// ------------------------
// Pure helpers that the Migration Wizard UI calls. Strictly NON-destructive
// for the existing data: this module only READS existing Wells/Rigs and
// returns a proposed grouping. Saving the migration is an explicit user
// action handled by the UI (which calls `applyMigrationPlan`).
//
// User requirement (Feb 2026 / RCE-1):
//   * DO NOT auto-migrate on first read.
//   * Migration must be a visible, explicit UI step.
//   * The user can defer ("Remind me later") or dismiss permanently.

import { listWellProfiles, updateWellProfile } from "@/lib/well/storage";
import { listRigSheets } from "@/lib/rigsheet/storage";
import {
  createProject, listProjects,
} from "./project";
import { emit, RCE_EVENTS } from "./events";
import { scoped } from "@/lib/accountScope";

export const MIGRATION_PROMPT_STATE_KEY = "rsc.rce.migrationPrompt.v1";

// Possible UI states of the non-blocking migration banner:
const PROMPT_STATES = ["pending", "snoozed", "dismissed", "completed"];

function _safeParse(s, f) { try { return JSON.parse(s) ?? f; } catch { return f; } }

/** Read the persistent prompt state ("pending" by default). */
export function getMigrationPromptState() {
  const raw = _safeParse(scoped.getItem(MIGRATION_PROMPT_STATE_KEY), null);
  if (!raw || typeof raw !== "object") return { status: "pending", updatedAt: null };
  const s = PROMPT_STATES.includes(raw.status) ? raw.status : "pending";
  return { status: s, updatedAt: raw.updatedAt || null };
}

/** Update the persistent prompt state. */
export function setMigrationPromptState(status) {
  if (!PROMPT_STATES.includes(status))
    throw new Error(`Migration prompt status must be one of: ${PROMPT_STATES.join(", ")}.`);
  const next = { status, updatedAt: new Date().toISOString() };
  scoped.setItem(MIGRATION_PROMPT_STATE_KEY, JSON.stringify(next));
  return next;
}

/**
 * Should the non-blocking banner be shown to the user right now?
 *
 * Rules:
 *   * dismissed / completed → no
 *   * snoozed               → no (UI may re-prompt after N days; RCE-1 just
 *                             keeps it hidden until explicitly cleared)
 *   * pending               → yes, but ONLY if at least one well or rig
 *                             carries a legacy operator/client/contractor
 *                             string that has NOT already been bound to a
 *                             Project Profile.
 */
export function shouldShowMigrationBanner() {
  const state = getMigrationPromptState();
  if (state.status !== "pending") return false;
  const plan = buildMigrationPlan();
  return plan.groups.length > 0;
}

/**
 * Build a proposed migration plan from existing data WITHOUT modifying
 * anything. The plan groups Wells (and Rig Sheets when they share the same
 * operator/client/field) into proposed Project Profiles.
 *
 * Output shape:
 *   {
 *     groups: [{
 *       proposedName, operator, client, country, field, block, contractor,
 *       wellIds: string[], rigSheetIds: string[],
 *     }],
 *     unboundWells: string[],
 *     unboundRigs:  string[],
 *   }
 */
export function buildMigrationPlan() {
  const wells = (typeof listWellProfiles === "function") ? safeList(listWellProfiles) : [];
  const rigs  = (typeof listRigSheets === "function")     ? safeList(listRigSheets)     : [];

  const groups = new Map(); // key → group

  const _keyFor = (operator, client, field, block) =>
    JSON.stringify([
      String(operator || "").trim().toLowerCase(),
      String(client   || "").trim().toLowerCase(),
      String(field    || "").trim().toLowerCase(),
      String(block    || "").trim().toLowerCase(),
    ]);

  for (const w of wells) {
    const operator = w.operator || "";
    if (!operator) continue;                           // skip empty
    if (w.projectId) continue;                         // already bound
    const key = _keyFor(operator, w.client, w.field, w.block);
    const g = groups.get(key) || {
      proposedName: _proposeName(operator, w.field, w.block),
      operator, client: w.client || "", country: w.country || "",
      field: w.field || "", block: w.block || "", contractor: w.rigContractor || "",
      wellIds: [], rigSheetIds: [],
    };
    g.wellIds.push(w.id);
    groups.set(key, g);
  }
  for (const r of rigs) {
    const p = r.payload || {};
    if (!p.operator) continue;
    if (r.projectId) continue;
    const key = _keyFor(p.operator, "", p.fieldOrBlock, "");
    const g = groups.get(key) || {
      proposedName: _proposeName(p.operator, p.fieldOrBlock, ""),
      operator: p.operator, client: "", country: p.country || "",
      field: p.fieldOrBlock || "", block: "", contractor: p.rigContractor || "",
      wellIds: [], rigSheetIds: [],
    };
    g.rigSheetIds.push(r.id);
    groups.set(key, g);
  }

  return {
    groups: [...groups.values()],
    unboundWells: wells.filter((w) => !w.projectId && !w.operator).map((w) => w.id),
    unboundRigs:  rigs.filter((r) => !r.projectId && !(r.payload || {}).operator).map((r) => r.id),
  };
}

function safeList(fn) { try { return fn(); } catch { return []; } }

function _proposeName(operator, field, block) {
  const parts = [String(operator || "").trim()];
  const tail = [field, block].filter((x) => x && String(x).trim()).join(" / ");
  if (tail) parts.push(tail);
  return parts.filter(Boolean).join(" — ");
}

/**
 * Apply a migration plan. Creates a Project Profile per group and writes
 * `projectId` onto every well in `wellIds`. Returns a summary of what was
 * created.
 *
 * Rig sheets do NOT carry a `projectId` field today, so RCE-1 only stamps
 * wells. Rig sheet binding lands in RCE-2 when the Rig Sheet schema gets a
 * `projectId` migration. The plan still returns the rig IDs so the wizard
 * can preview the association without mutating rig sheets.
 *
 * Idempotent: if a group's wells already point at a Project Profile, those
 * wells are left alone.
 */
export function applyMigrationPlan(groups, opts = {}) {
  if (!Array.isArray(groups)) throw new Error("applyMigrationPlan: groups must be an array.");
  const onlyKeyed = opts.onlyKeys instanceof Set ? opts.onlyKeys : null;
  const results = [];
  for (const g of groups) {
    if (onlyKeyed && !onlyKeyed.has(_groupKey(g))) continue;
    if (!g || !g.operator) continue;
    const project = createProject({
      name: g.proposedName || g.operator,
      operator: g.operator,
      client: g.client, country: g.country, field: g.field,
      block: g.block, contractor: g.contractor,
    });
    const wellsBound = [];
    for (const wid of g.wellIds || []) {
      try { updateWellProfile(wid, { projectId: project.id }); wellsBound.push(wid); }
      catch { /* leave well untouched on failure */ }
    }
    results.push({
      projectId: project.id,
      projectName: project.name,
      wellsBound,
      rigSheetIds: g.rigSheetIds || [],
    });
  }
  if (results.length > 0) setMigrationPromptState("completed");
  try { emit(RCE_EVENTS.MIGRATION_COMPLETED, { results }); } catch { /* noop */ }
  return { ok: true, created: results };
}

function _groupKey(g) {
  return [g.operator, g.client, g.field, g.block]
    .map((x) => String(x || "").trim().toLowerCase()).join("|");
}

/** Test/diagnostic helper. */
export function _clearMigrationPromptState() {
  scoped.removeItem(MIGRATION_PROMPT_STATE_KEY);
}

export { listProjects };
